import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {map, Observable, of, switchMap, zip} from 'rxjs';
import {PolicyCartConfig, PolicyDepartment, PolicySubType, PolicyType} from './policy_types';
import {CustomerPolicy} from 'src/app/px/claims/services/claim-intimation';
import {ENVIRONMENT} from 'src/environments/environment.token';

@Injectable({
  providedIn: 'root',
})
export class PolicyTypeService {
  private httpClient = inject(HttpClient);
  private environment = inject(ENVIRONMENT);

  private policyDepartments: Map<number, PolicyDepartment> = new Map<number, PolicyDepartment>();
  private saibaPolicyDepartments: Map<string, PolicyDepartment> = new Map<string, PolicyDepartment>();

  private policyTypes: Map<number, PolicyType> = new Map<number, PolicyType>();
  private saibaPolicyTypes = new Map<boolean, Map<number, PolicyType>>();

  private policyTypeIdsByDepartmentId: Map<number, number[]> = new Map<number, number[]>();
  private saibaPolicyTypeIdsByIsLiDepartmentId = new Map<number, Map<boolean, number[]>>();

  private policySubTypesByPolicyTypeId: Map<number, number[]> = new Map<number, number[]>();
  private policySubtypes: Map<number, PolicySubType> = new Map<number, PolicySubType>();

  // Policy Types

  getAllPolicyDepartments(): PolicyDepartment[] {
    return Array.from(this.policyDepartments.values())
      .sort((p1, p2) => p1.name.localeCompare(p2.name));
  }

  getAllSaibaPolicyDepartments(): PolicyDepartment[] {
    return Array.from(this.saibaPolicyDepartments.values())
      .sort((p1, p2) => p1.name.localeCompare(p2.name));
  }

  getPolicyDepartment(policyDepartmentId: number): PolicyDepartment {
    return this.policyDepartments.get(policyDepartmentId)!;
  }

  updateDepartment(department: PolicyDepartment) {
    return this.httpClient.post(`${this.environment.appConfig.apiBaseUrl}/policy-department`, department).pipe(
      switchMap(() => {
        this.policyDepartments.clear();
        this.policyTypes.clear();
        this.saibaPolicyDepartments.clear();
        this.saibaPolicyTypes.clear();

        return zip(this.loadPolicyDepartmentsAndTypes(true), this.loadSaibaPolicyDepartmentsAndTypes());
      })
    );
  }

  // Policy Types

  getPolicyTypesForPolicyDepartment(policyDepartmentId?: number): PolicyType[] {
    if (policyDepartmentId === undefined) {
      return [];
    }

    return this.policyTypeIdsByDepartmentId.get(policyDepartmentId)?.map((id) => this.policyTypes.get(id))
      .filter((i) => i)
      .map((i) => i!)
      .sort((p1, p2) => p1.name.localeCompare(p2.name)) || [];
  }

  getSaibaPolicyTypesForPolicyDepartment(policyDepartmentId?: number, isLi?: boolean): PolicyType[] {
    if (policyDepartmentId == null || isLi == null) {
      return [];
    }

    return this.saibaPolicyTypeIdsByIsLiDepartmentId
      .get(policyDepartmentId)!
      .get(isLi)!
      .map((id) => this.saibaPolicyTypes.get(isLi)!.get(id)!)
      .sort((p1, p2) => p1.name.localeCompare(p2.name));
  }

  getPolicyType(policyTypeId: number): PolicyType {
    return this.policyTypes.get(policyTypeId)!;
  }

  updatePolicyType(type: PolicyType) {
    return this.httpClient.post(`${this.environment.appConfig.apiBaseUrl}/policy-type`, type).pipe(
      switchMap(() => {
        this.policyDepartments.clear();
        this.policyTypes.clear();
        this.saibaPolicyDepartments.clear();
        this.saibaPolicyTypes.clear();

        return zip(this.loadPolicyDepartmentsAndTypes(true), this.loadSaibaPolicyDepartmentsAndTypes());
      })
    );
  }

  // Policy Sub Types

  getPolicySubtypesTypesForPolicyType(policyTypeId?: number): PolicySubType[] {
    if (policyTypeId === undefined) {
      return [];
    }

    return this.policySubTypesByPolicyTypeId.get(policyTypeId)?.map((id) => this.policySubtypes.get(id))
      .filter((i) => i)
      .map((i) => i!)
      .sort((p1, p2) => p1.name.localeCompare(p2.name)) || [];
  }

  getPolicySubType(policySubTypeId: number): PolicySubType {
    return this.policySubtypes.get(policySubTypeId)!;
  }

  updatePolicySubType(type: PolicySubType) {
    return this.httpClient.post(`${this.environment.appConfig.apiBaseUrl}/policy-sub-type`, type).pipe(
      switchMap(() => {
        return zip(this.loadPolicyDepartmentsAndTypes(true), this.loadSaibaPolicyDepartmentsAndTypes());
      })
    );
  }

  // Loading

  loadPolicyDepartmentsAndTypes(includeInactive: boolean): Observable<boolean> {
    let includeInactiveValue = includeInactive ? 1 : 0;

    return this.httpClient.get<PolicyDepartment[]>(`${this.environment.appConfig.apiBaseUrl}/policy-department`, {
      params: {
        include_inactive: includeInactiveValue,
      },
    }).pipe(map((policyDepartments) => {
      this.policyDepartments.clear();
      this.policyTypes.clear();
      this.policySubtypes.clear();

      this.policyTypeIdsByDepartmentId.clear();
      this.policySubTypesByPolicyTypeId.clear();

      policyDepartments.forEach((department) => {
        this.policyDepartments.set(department.id, department);
        this.policyTypeIdsByDepartmentId.set(department.id, department.policyTypes?.map((type) => {
          this.policyTypes.set(type.id, type);

          return type.id;
        }));

        department.policyTypes?.forEach((type) => {
          this.policySubTypesByPolicyTypeId.set(type.id, type.policySubTypes?.map((subType) => {
            this.policySubtypes.set(subType.id, subType);

            return subType.id;
          }));
        });
      });

      return true;
    }));
  }

  loadSaibaPolicyDepartmentsAndTypes() {
    if (this.saibaPolicyDepartments.size > 0 && this.saibaPolicyTypes.size > 0) {
      return of(true);
    }

    return this.httpClient.get<PolicyDepartment[]>(`${this.environment.appConfig.apiBaseUrl}/saiba-department`).pipe(map((policyDepartments) => {
      this.saibaPolicyDepartments.clear();
      this.saibaPolicyTypes.clear();
      this.saibaPolicyTypeIdsByIsLiDepartmentId.clear();

      policyDepartments.forEach((department) => {
        this.saibaPolicyDepartments.set(department.name, department);
        if (!this.saibaPolicyTypeIdsByIsLiDepartmentId.has(department.id)) {
          this.saibaPolicyTypeIdsByIsLiDepartmentId.set(department.id, new Map<boolean, number[]>());
        }

        this.saibaPolicyTypeIdsByIsLiDepartmentId.get(department.id)!
          .set(department.isLi, department.policyTypes.map((type) => {
            if (!this.saibaPolicyTypes.has(department.isLi)) {
              this.saibaPolicyTypes.set(department.isLi, new Map<number, PolicyType>());
            }
            this.saibaPolicyTypes.get(department.isLi)!.set(type.id, type);

            return type.id;
          }));
      });

      return true;
    }));
  }

  getActivePolicies(
    customerGroupId?: number,
    serviceDepartmentId?: number,
    policyId?: number,
    shallowReadonly?: boolean
  ): Observable<CustomerPolicy[]> {
    const params: any = {
    };

    if (customerGroupId) {
      params.customer_group_id = customerGroupId;
    }

    if (serviceDepartmentId !== undefined) {
      params.service_department_id = serviceDepartmentId;
    }
    if (policyId !== undefined) {
      params.policy_id = policyId;
    }
    if (shallowReadonly) {
      params.shallow_read = true;
    }

    return this.httpClient.get<CustomerPolicy[]>(`${this.environment.appConfig.apiBaseUrl}/v1/policies`, {
      params,
    });
  }

  getPolicyConfiguration(): Observable<PolicyCartConfig[]> {
    return this.httpClient.get<PolicyCartConfig[]>(`${this.environment.appConfig.apiBaseUrl}/policy-type-cart-config`);
  }
}

